import { useDispatch } from 'react-redux';
import { MessageMedium } from 'constants/messageMediums';
import { CampaignType } from 'modules/domains/campaign';
import { TemplateEditViews, UpdateMediumFieldsType } from 'modules/templateEditor/common';
import InStudioTemplateEditing from 'modules/templateEditor/integrations/InStudioTemplateEditing';
import { addNegativeToast } from 'modules/toastContainer';
import noop from 'utils/noop';
import { useTemplateTakeover } from 'modules/templateEditor/integrations/InStudioTemplateEditing/context/TemplateTakeoverContext';
import { Dispatch, SetStateAction } from 'react';
import { useFeatureFlags, useCampaign } from '../BaseSendEditor.hooks';
import AttachTemplateTakeover from './AttachTemplateTakeover';

export interface TemplateTakeoverProps {
  /** The id of the associated campaign */
  campaignId?: number;
  /** The id of the chosen template */
  templateId?: number;
  /** The placement id.  Currently only used in embedded messaging. */
  placementId?: number;
  /** The message medium for the campaign (EG: email, sms etc) */
  messageMedium: MessageMedium;
  /** Callback when the takeover is closed */
  onClose?: (newId?: number) => void;
  /** Callback when a template is selected */
  onAttachTemplate?: (newId?: number) => void;
  currentLocale?: string;
  setCurrentLocale: Dispatch<SetStateAction<string | undefined>>;
  /** NBA Suggested template content */
  suggestedContent?: UpdateMediumFieldsType;
}

export const TemplateTakeover = ({
  templateId,
  campaignId,
  messageMedium,
  onClose = noop,
  onAttachTemplate = noop,
  currentLocale,
  setCurrentLocale = noop,
  placementId,
  suggestedContent,
}: TemplateTakeoverProps) => {
  const dispatch = useDispatch();
  const { campaign } = useCampaign();
  const { studioTemplateEditingIsEnabled } = useFeatureFlags();
  const takeover = useTemplateTakeover();

  const handleAttachTemplateId = (newId?: number) => {
    if (!templateId && !newId) {
      // display error toast and don't close the takeover if no
      // template has previously been added and none are now selected
      dispatch(addNegativeToast('Please select a template to attach to this campaign.'));
    } else {
      onAttachTemplate(newId);
    }
  };

  const handleAttachTemplate = (template: { id: number; locale: string }) =>
    handleAttachTemplateId(template.id);

  const handleClose = () => {
    takeover.close();
    onClose();
  };

  if (!takeover.isOpen) return null;

  return studioTemplateEditingIsEnabled ? (
    <InStudioTemplateEditing
      attachedTemplateId={templateId}
      campaignId={campaignId}
      messageMedium={messageMedium}
      templateId={takeover.view === TemplateEditViews.select ? null : templateId}
      title={campaign?.name}
      view={takeover.view}
      onViewChange={takeover.setView}
      // Callbacks
      onAttachTemplate={handleAttachTemplate}
      onClose={handleClose}
      currentLocale={currentLocale}
      setCurrentLocale={setCurrentLocale}
      placementId={placementId}
      campaignType={campaign.campaignTypeDefinition ?? CampaignType.WorkflowTriggered}
      suggestedContent={suggestedContent}
    />
  ) : (
    <AttachTemplateTakeover
      messageMedium={messageMedium}
      templateId={templateId}
      // Callbacks
      onAttachTemplate={handleAttachTemplateId}
      onClose={handleClose}
      placementId={placementId}
    />
  );
};

export default TemplateTakeover;
